// Single source of truth for "is the Roava BACKEND actually online?".
//
// Important: this probe targets the Supabase backend (data/auth/functions),
// NOT the native shell host (roava.lovable.app). The two can disagree:
//   - native [RoavaShell] remote probe → roava.lovable.app (Cloudflare static)
//   - JS [connectivity] backend probe → Supabase REST endpoint
// A reachable shell host with an unreachable backend is a legitimate state
// (captive portal that lets Cloudflare through, Supabase region outage, etc.)
// — that's why every log here is prefixed `backend(supabase)`.
//
// Wi-Fi link up != backend reachable. Airline/hotel/captive networks join
// instantly but route all traffic to a splash page. This module pairs the
// Capacitor Network link state with the existing isSupabaseReachable() probe
// and exposes one reactive store every consumer can subscribe to.
//
// Strict rules:
//   - Edge-only side effects: auth refresh + outbox drain fire ONLY on the
//     backendReachable !== true → true transition.
//   - No new probe implementation — reuses isSupabaseReachable().
//   - No mutation of auth, cached trip, or queued pings.
//
// OTA-safe: pure JS.

import { useSyncExternalStore } from "react";
import {
  addConnectionListener,
  getConnectionType,
  isSupabaseReachable,
  type ConnectionType,
} from "@/lib/native/network";
import { drainPingOutbox } from "@/lib/native/ping-outbox";

export type Reachability = {
  link: ConnectionType;
  /** null = not probed yet, true/false = last probe result. */
  backendReachable: boolean | null;
  lastProbeAt: number;
};

const CAPTIVE_POLL_MS = 30_000;
const PROBE_TIMEOUT_MS = 3_000;

let state: Reachability = {
  link: "unknown",
  backendReachable: null,
  lastProbeAt: 0,
};
let snapshot: Reachability = state;
const listeners = new Set<() => void>();

function commit(next: Partial<Reachability>): Reachability {
  state = { ...state, ...next };
  snapshot = { ...state };
  listeners.forEach((fn) => { try { fn(); } catch { /* ignore */ } });
  return snapshot;
}

export function getReachabilitySnapshot(): Reachability {
  return snapshot;
}

function subscribe(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

export function useReachability(): Reachability {
  return useSyncExternalStore(subscribe, getReachabilitySnapshot, getReachabilitySnapshot);
}

let probing = false;
let captivePollTimer: number | null = null;
let lastLoggedLink: { link: ConnectionType; connected: boolean } | null = null;
// Debounce: require 2 consecutive failed probes before flipping to unreachable.
// A single transient timeout (cold radio, app resume) no longer trips the
// captive-portal banner on a healthy network.
const FAIL_THRESHOLD = 2;
let consecutiveFails = 0;

function logLinkEdge(link: ConnectionType) {
  const connected = link !== "none" && link !== "unknown";
  const linkName = link === "unknown" ? "none" : link;
  if (
    lastLoggedLink &&
    lastLoggedLink.link === link &&
    lastLoggedLink.connected === connected
  ) return;
  lastLoggedLink = { link, connected };
  // eslint-disable-next-line no-console
  console.log(`[connectivity] link ${linkName} connected=${connected}`);
}

function schedCaptivePoll(enable: boolean) {
  if (!enable) {
    if (captivePollTimer != null) {
      window.clearInterval(captivePollTimer);
      captivePollTimer = null;
    }
    return;
  }
  if (captivePollTimer != null) return;
  captivePollTimer = window.setInterval(() => {
    void probeReachabilityNow("captive-poll");
  }, CAPTIVE_POLL_MS);
}

/** Run a single reachability probe. Edge-emits logs + side effects. */
export async function probeReachabilityNow(_reason: string = "manual"): Promise<boolean> {
  if (probing) return state.backendReachable === true;
  probing = true;
  const previous = state.backendReachable;
  try {
    // If the link itself is down, skip the network probe — it can only fail
    // and would just produce noise. Treat as unreachable without logging
    // unless this is the first observation.
    if (state.link === "none") {
      if (previous !== false) {
        commit({ backendReachable: false, lastProbeAt: Date.now() });
        // eslint-disable-next-line no-console
        console.log("[connectivity] backend(supabase) reachable=false reason=backend-supabase-unreachable");
      }
      schedCaptivePoll(false);
      return false;
    }

    const ok = await isSupabaseReachable(PROBE_TIMEOUT_MS);

    if (ok) {
      consecutiveFails = 0;
      commit({ backendReachable: true, lastProbeAt: Date.now() });
      if (previous !== true) {
        // eslint-disable-next-line no-console
        console.log("[connectivity] backend(supabase) reachable=true decision=refresh-and-sync");
        schedCaptivePoll(false);
        if (typeof window !== "undefined") {
          window.dispatchEvent(new CustomEvent("roava:backend-reachable"));
        }
        void drainPingOutbox().catch(() => {});
      } else {
        schedCaptivePoll(false);
      }
      return true;
    }

    // Failed probe — debounce before flipping to unreachable.
    consecutiveFails += 1;
    // eslint-disable-next-line no-console
    console.log(`[connectivity] backend(supabase) probe failed attempt=${consecutiveFails}/${FAIL_THRESHOLD}`);

    if (previous === true && consecutiveFails < FAIL_THRESHOLD) {
      // Still treated as reachable; just keep probing.
      commit({ lastProbeAt: Date.now() });
      schedCaptivePoll(true);
      return true;
    }

    commit({ backendReachable: false, lastProbeAt: Date.now() });
    if (previous !== false) {
      // eslint-disable-next-line no-console
      console.log("[connectivity] backend(supabase) reachable=false reason=backend-supabase-unreachable");
    }
    schedCaptivePoll(true);
    return false;
  } finally {
    probing = false;
  }
}

let initialized = false;

export function initReachability(): void {
  if (initialized) return;
  initialized = true;

  // Seed link state and probe immediately.
  void getConnectionType().then((t) => {
    commit({ link: t });
    logLinkEdge(t);
    void probeReachabilityNow("init");
  });

  // Link change subscription. Re-probe on any transition.
  addConnectionListener((t) => {
    const prevLink = state.link;
    commit({ link: t });
    logLinkEdge(t);
    if (t === "none") {
      // Link gone — mark unreachable without a probe (would only fail).
      if (state.backendReachable !== false) {
        commit({ backendReachable: false, lastProbeAt: Date.now() });
        // eslint-disable-next-line no-console
        console.log("[connectivity] backend(supabase) reachable=false reason=backend-supabase-unreachable");
      }
      schedCaptivePoll(false);
      return;
    }
    if (prevLink !== t) {
      void probeReachabilityNow("link-change");
    }
  });

  if (typeof window !== "undefined") {
    window.addEventListener("online", () => { void probeReachabilityNow("online-event"); });
    window.addEventListener("offline", () => {
      commit({ link: "none" });
      logLinkEdge("none");
      if (state.backendReachable !== false) {
        commit({ backendReachable: false, lastProbeAt: Date.now() });
        // eslint-disable-next-line no-console
        console.log("[connectivity] backend(supabase) reachable=false reason=backend-supabase-unreachable");
      }
      schedCaptivePoll(false);
    });
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") {
          void probeReachabilityNow("visibility");
        }
      });
    }
  }
}
